/**
 * Parser for NDBC's `latest_obs.txt` — every reporting station in one file.
 *
 * A different layout from the per-station `realtime2` files, and the columns are
 * *not* in the same order: this file carries LAT/LON up front and puts PTDY
 * between PRES and ATMP, where realtime2 has it near the end. Reusing the other
 * parser reads air temperature as pressure tendency.
 *
 *   #STN  LAT  LON  YYYY MM DD hh mm  WDIR WSPD GST  WVHT DPD APD MWD
 *   PRES  PTDY ATMP WTMP DEWP VIS TIDE
 *
 * This is an **hourly rollup**, not a live feed — half its stations share the
 * same minute-of-hour, which is a publishing schedule rather than a coincidence.
 * Readings run about 70 minutes old against 10 for a CO-OPS station fetched
 * directly, so anything drawn from here has to be labelled as the slower source
 * it is.
 */

const MS_TO_KT = 1.94384;

export interface BulkObservation {
  id: string;
  lat: number;
  lon: number;
  t: Date;
  /**
   * Null on a wave buoy. CDIP's hulls carry no anemometer, so most of the wave
   * coverage between San Francisco and San Diego reports height and nothing
   * else — dropping them for want of wind cost the waves filter its stations.
   */
  speedKt: number | null;
  dirDeg: number | null;
  gustKt: number | null;
  waveHeightM: number | null;
  airTempC: number | null;
  waterTempC: number | null;
}

/** Column positions, zero-based. Named because the two NDBC layouts differ. */
const COL = {
  id: 0, lat: 1, lon: 2, year: 3, month: 4, day: 5, hour: 6, minute: 7,
  wdir: 8, wspd: 9, gst: 10, wvht: 11, atmp: 17, wtmp: 18,
} as const;

function num(v: string | undefined): number | null {
  if (v === undefined || v === 'MM') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

export function parseLatestObs(text: string, now: Date = new Date()): BulkObservation[] {
  const out: BulkObservation[] = [];
  // Guards against a clock-skewed or malformed row landing far in the future.
  const horizon = now.getTime() + 6 * 3_600_000;

  for (const raw of text.split('\n')) {
    const line = raw.trim();
    if (line === '' || line.startsWith('#')) continue;

    const c = line.split(/\s+/);
    if (c.length < COL.wspd + 1) continue;

    const lat = num(c[COL.lat]);
    const lon = num(c[COL.lon]);
    // Without a position there is nowhere to put it.
    if (lat === null || lon === null) continue;

    const parts = [COL.year, COL.month, COL.day, COL.hour, COL.minute].map((i) => Number(c[i]));
    if (parts.some((n) => !Number.isFinite(n))) continue;
    const t = Date.UTC(parts[0], parts[1] - 1, parts[2], parts[3], parts[4]);
    if (!Number.isFinite(t) || t > horizon) continue;

    const speedMs = num(c[COL.wspd]);
    const waveHeightM = num(c[COL.wvht]);
    const airTempC = num(c[COL.atmp]);
    const waterTempC = num(c[COL.wtmp]);
    // A row reporting none of the four is a station on the air with nothing to
    // say; keeping it would put a marker on the map that can never show a value.
    if (speedMs === null && waveHeightM === null && airTempC === null && waterTempC === null) {
      continue;
    }

    const gustMs = num(c[COL.gst]);
    out.push({
      id: c[COL.id].toUpperCase(),
      lat,
      lon,
      t: new Date(t),
      speedKt: speedMs === null ? null : round1(speedMs * MS_TO_KT),
      dirDeg: num(c[COL.wdir]),
      gustKt: gustMs === null ? null : round1(gustMs * MS_TO_KT),
      waveHeightM,
      airTempC,
      waterTempC,
    });
  }

  return out;
}

/** Kilometres between two points, flat-earth over the distances involved here. */
function km(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const mid = ((a.lat + b.lat) / 2) * (Math.PI / 180);
  return Math.hypot((a.lat - b.lat) * 110.54, (a.lon - b.lon) * 111.32 * Math.cos(mid));
}

/**
 * Drops national stations that sit on top of one we already show.
 *
 * NDBC republishes most of the CO-OPS network under C-MAN ids — every one of the
 * Bay's fourteen has a twin at 0.0 km, and `FTPC1` is literally named "9414290 -
 * San Francisco, CA". Left in, the Bay would carry two markers per station whose
 * readings disagree, because the republished copy is an hour behind the original.
 *
 * Proximity rather than an id list, so adding a curated station keeps working
 * without anyone remembering to also exclude its shadow.
 */
export function withoutCurated<T extends { lat: number; lon: number }>(
  bulk: T[],
  curated: ReadonlyArray<{ lat: number; lon: number }>,
  minKm = 2,
): T[] {
  return bulk.filter((s) => !curated.some((c) => km(s, c) < minKm));
}
